<!--
  =====================================================================
  INFORMATIVE AL PUBBLICO SINGLE PAGE - REGULATORY DOCUMENT VIEW
  =====================================================================
  
  Displays detailed information about regulatory documents for public information.
  
  FEATURES:
  - Dynamic informativa lookup from URL slug
  - Multi-image carousel with smart placeholder detection
  - Flexible image handling (single or array format)
  - Back button with smart text hiding on scroll
  - Responsive image gallery with dots and arrows
  - Dynamic section content rendering (HTML-based)
  - Shows loading/error/not-found states
  - Back button visibility management
  
  URL PATTERN: /cassa/informative-al-pubblico/:slug
  
  =====================================================================
-->

<template>
  <div>
    <SiteHeader />

    <main class="informativa-detail">
      <div
        class="informativa-back-top"
        v-show="!isMegamenuOpen && !isFooterButtonVisible"
      >
        <a :class="['back-to-informative', { compact: hideBackText }]" href="/cassa/informative-al-pubblico">
          <span class="arrow">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <span class="label">Torna alle informative</span>
        </a>
      </div>

      <div v-if="state.loading" class="loading-state">Caricamento...</div>
      
      <div v-else-if="state.error" class="error-state">Errore durante il caricamento dell'informativa</div>

      <div v-else class="informativa-content">
        <div class="informativa-header">
          <p class="informativa-header__eyebrow" v-if="state.page.eyebrow">{{ state.page.eyebrow }}</p>
          <h1 class="informativa-header__title">{{ state.page.title }}</h1>
        </div>

        <div v-if="shouldShowCarousel" class="informativa-image-carousel">
          <div v-if="isMultipleImages" class="carousel-container">
            <button 
              v-show="carouselIndex > 0"
              class="carousel-arrow carousel-arrow-left" 
              @click="carouselIndex--"
              aria-label="Immagine precedente"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <div class="carousel-image-wrapper">
              <img 
                :src="currentCarouselImage" 
                :alt="state.page.title" 
                class="carousel-image"
              />
            </div>

            <button 
              v-show="carouselIndex < state.page.image.length - 1"
              class="carousel-arrow carousel-arrow-right" 
              @click="carouselIndex++"
              aria-label="Immagine successiva"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <div v-else class="single-image-wrapper">
            <img 
              :src="currentCarouselImage" 
              :alt="state.page.title" 
              class="carousel-image"
            />
          </div>

          <div v-if="isMultipleImages" class="carousel-dots">
            <button
              v-for="(_, index) in state.page.image"
              :key="index"
              class="dot"
              :class="{ active: carouselIndex === index }"
              @click="carouselIndex = index"
              :aria-label="`Immagine ${index + 1}`"
            />
          </div>
        </div>

        <div class="informativa-sections">
          <component
            v-for="(section, index) in state.pageSections"
            :key="index"
            :is="getComponent(section.type)"
            :content="section.content"
          />
        </div>
      </div>
    </main>

    <SiteFooter />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed } from 'vue'
import { state, loadPageContent, setInformativaPageData, setNotFound } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// SHARED LAYOUT COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SiteHeader      from '@/components/sections/shared/SiteHeader.vue'
import SiteFooter      from '@/components/sections/shared/SiteFooter.vue'

// ───────────────────────────────────────────────────────────────────
// HTML-BASED SECTION COMPONENTS
// ───────────────────────────────────────────────────────────────────
import HtmlTextSection from '@/components/sections/html/SectionHtmlText.vue'
import SectionDocumentHtml from '@/components/sections/html/SectionDocumentHtml.vue'

// ───────────────────────────────────────────────────────────────────
// COMPONENT STATE
// ───────────────────────────────────────────────────────────────────

const informativaFound = ref(false)
const isMegamenuOpen = ref(false)
const isFooterButtonVisible = ref(false)
const hideBackText = ref(false)
const carouselIndex = ref(0)

// ───────────────────────────────────────────────────────────────────
// PLACEHOLDER IMAGE DETECTION HELPERS
// ───────────────────────────────────────────────────────────────────

/**
 * Extracts filename from URL (handles absolute/relative URLs, removes query strings)
 * 
 * @param {string} src - The image source URL
 * @returns {string} The filename without path or query parameters
 */
function getBasename(src) {
  try {
    const u = new URL(src, window.location.origin)
    const cleanPath = u.pathname
    return cleanPath.substring(cleanPath.lastIndexOf('/') + 1)
  } catch (e) {
    const noQuery = String(src).split('?')[0].split('#')[0]
    const parts = noQuery.split('/')
    return parts[parts.length - 1] || noQuery
  }
}

/**
 * Regular expression to identify placeholder images
 * Matches: placeholder_informativa, placeholder-informativa, with optional _number suffix
 * Supports common image extensions: png, jpg, jpeg, webp
 */
const PLACEHOLDER_RE = /^placeholder[_-]?informativa(?:_\d+)?\.(png|jpg|jpeg|webp)$/i

/**
 * Determines if an image is a placeholder (used for filtering)
 * 
 * @param {string} src - The image source URL
 * @returns {boolean} True if image is identified as placeholder
 */
function isPlaceholderImage(src) {
  const name = getBasename(src)
  return PLACEHOLDER_RE.test(name)
}

// ───────────────────────────────────────────────────────────────────
// CAROUSEL IMAGE LOGIC
// ───────────────────────────────────────────────────────────────────

/**
 * Determines if carousel should show multiple images or single image display
 * Handles both string and array image formats
 * @returns {boolean} True if carousel should display
 */
const isMultipleImages = computed(() => {
  return Array.isArray(state.page.image) && state.page.image.length > 1
})

/**
 * Smart carousel visibility control
 * Hides carousel if:
 * - No images available
 * - All images are placeholders (filtered out)
 * 
 * @returns {boolean} True if carousel should be visible
 */
const shouldShowCarousel = computed(() => {
  if (!Array.isArray(state.page.image) && typeof state.page.image === 'string') {
    // If single string, check if it's not a placeholder
    return state.page.image && !isPlaceholderImage(state.page.image)
  }
  if (!Array.isArray(state.page.image) || state.page.image.length === 0) return false
  // Don't show if all images are placeholders
  return !state.page.image.every(img => isPlaceholderImage(img))
})

/**
 * Gets current carousel image based on navigation index
 * Handles both string and array formats
 * 
 * @returns {string|null} Current image URL or null if unavailable
 */
const currentCarouselImage = computed(() => {
  if (typeof state.page.image === 'string') {
    return state.page.image || null
  }
  if (!Array.isArray(state.page.image) || state.page.image.length === 0) return null
  return state.page.image[carouselIndex.value] || null
})

// ───────────────────────────────────────────────────────────────────
// BACK BUTTON VISIBILITY MANAGEMENT
// ───────────────────────────────────────────────────────────────────

/**
 * Checks megamenu and footer visibility to control back button display
 * Also hides back button label when scrolled past threshold
 */
const checkMegamenuAndFooter = () => {
  const megamenu = document.querySelector('.mega-menu')
  isMegamenuOpen.value = !!megamenu

  const footerButton = document.querySelector('footer .informativa-footer__link')
  if (footerButton) {
    const rect = footerButton.getBoundingClientRect()
    isFooterButtonVisible.value = rect.top >= 0 && rect.top <= window.innerHeight
  }

  // Hide back button label after scrolling 140px
  hideBackText.value = window.scrollY > 140
}

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

/**
 * Initialize page on component mount
 * - Extract informativa slug from URL
 * - Load informativa index from store
 * - Find and set specific informativa data
 * - Setup DOM observers for visibility management
 */
onMounted(async () => {
  // Parse URL: /cassa/informative-al-pubblico/:slug
  const pathSegments = window.location.pathname.split('/').filter(Boolean)
  const segments = pathSegments[0] === 'cassa' ? pathSegments.slice(1) : pathSegments
  const slug = segments[1]
  
  if (slug && slug !== 'informative-al-pubblico') {
    await loadPageContent('/informative-al-pubblico')
    informativaFound.value = setInformativaPageData(slug)
    
    // If informativa not found, trigger centralized 404 handling
    if (!informativaFound.value) {
      setNotFound()
      return
    }
  }

  // Setup DOM observer for megamenu and footer visibility
  const observer = new MutationObserver(checkMegamenuAndFooter)
  observer.observe(document.body, {
    childList: true,
    attributes: true,
    attributeFilter: ['class'],
    subtree: true
  })
  
  // Setup scroll/resize listeners
  window.addEventListener('scroll', checkMegamenuAndFooter)
  window.addEventListener('resize', checkMegamenuAndFooter)
  setTimeout(checkMegamenuAndFooter, 100)

  // Cleanup on unmount
  onUnmounted(() => {
    observer.disconnect()
    window.removeEventListener('scroll', checkMegamenuAndFooter)
  })
})

// ───────────────────────────────────────────────────────────────────
// COMPONENT RESOLVER
// ───────────────────────────────────────────────────────────────────

/**
 * Maps section content types to Vue components
 * 
 * @param {string} type - The section type identifier
 * @returns {VueComponent} The component to render
 */
function getComponent(type) {
  switch (type) {
    case 'html-text': return HtmlTextSection
    case 'section-document-html': return SectionDocumentHtml
    default:          return HtmlTextSection
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/partials/variables';
@import '@/assets/styles/partials/compat';

// ─────────────────────────────────────────────────────────────────
// MAIN CONTAINER
// ─────────────────────────────────────────────────────────────────
.informativa-detail {
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: $space-2 $container-padding-x;

  @media (min-width: 1025px) and (max-width: 1300px) {
    padding-left: clamp(2rem, 4vw, 3rem) !important;
    padding-right: clamp(2rem, 4vw, 3rem) !important;
  }

  @media (max-width: 768px) {
    padding: $space-1 $container-padding-x-mob;
  }
}

// ─────────────────────────────────────────────────────────────────
// BACK BUTTON STYLING
// ─────────────────────────────────────────────────────────────────
.informativa-back-top {
  position: fixed;
  top: calc(var(--header-total-height) + 1.2rem);
  left: 1rem;
  z-index: 150;

  .back-to-informative {
    font-size: clamp(0.95rem, 2.5vw, 1.15rem);
    font-weight: 600;
    color: $clr-brand-dark;
    text-decoration: none;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    @include flex-gap(0.35rem);
    line-height: 1;
    padding: 0.6rem 0;

    @media (hover: hover) {
      &:hover { color: $clr-tertiary; transform: scale(1.05); }
    }

    .arrow {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1em;
      height: 1em;
      transition: transform 0.3s ease;
      transform: rotate(90deg);

      svg {
        width: 100%;
        height: 100%;
        display: block;
      }
    }

    .label {
      display: inline-block;
      white-space: nowrap;
      transition: opacity 0.25s ease, transform 0.25s ease;
      will-change: opacity, transform;
    }

    &.compact {
      @include flex-gap(0);
    }

    &.compact .label {
      opacity: 0;
      transform: translateX(-6px);
      pointer-events: none;
    }

    @media (max-width: 1700px) {
      @include flex-gap(0);

      .label {
        opacity: 0;
        transform: translateX(-6px);
        pointer-events: none;
      }
    }

    @media (hover: hover) {
      &:hover .arrow {
        transform: rotate(-90deg);
      }
    }
  }

  @media (max-width: 1300px) {
    top: calc(var(--header-total-height) + 1.2rem);
    left: 1rem;
  }

  @media (max-width: 920px) {
    display: none;
  }

  @media (max-width: 768px) {
    left: 0;
  }
}

// ─────────────────────────────────────────────────────────────────
// LOADING / ERROR / NOT FOUND STATES
// ─────────────────────────────────────────────────────────────────
.loading-state,
.error-state {
  min-height: calc(100vh - var(--header-total-height) - var(--footer-total-height));
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $space-3 $container-padding-x;
  text-align: center;
  color: $text-color;
  font-size: $font-title;
  font-weight: 600;
  line-height: 1.2;
}

.not-found {
  min-height: calc(100vh - var(--header-total-height) - var(--footer-total-height));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  @include flex-gap($space-0-5, column);
  padding: $space-3 $container-padding-x;
  
  @media (max-width: 768px) {
    padding: $space-3 $space-0-5;
  }
  
  &__title {
    color: $clr-brand-dark;
    font-size: $font-title;
    margin: 0;
    font-weight: 600;
    line-height: 1.2;
    
    @media (max-width: 920px) {
      font-size: $font-title-sm;
    }
    
    @media (max-width: 480px) {
      font-size: $font-title-xs;
    }
  }
  
  &__text {
    color: $clr-text-light;
    font-size: $font-lg;
    margin: 0;
    line-height: 1.35;
    
    @media (max-width: 768px) {
      font-size: $font-base-sm;
    }
  }
  
  &__link {
    display: inline-flex;
    align-items: center;
    @include flex-gap(0.35rem);
    font-size: clamp(0.95rem, 2.5vw, 1.15rem);
    font-weight: 600;
    color: $clr-brand-dark;
    text-decoration: none;
    transition: all 0.25s ease;
    padding: 0.6rem 0;
    
    @media (hover: hover) {
      &:hover {
        color: $clr-tertiary;
        transform: translateX(-4px);
      }

      &:hover .arrow {
        transform: rotate(-90deg);
      }
    }

    .arrow {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1em;
      height: 1em;
      transition: transform 0.3s ease;
      transform: rotate(90deg);

      svg {
        width: 100%;
        height: 100%;
        display: block;
      }
    }

    @media (max-width: 1500px) {
      @include flex-gap(0);

      span {
        opacity: 0;
        transform: translateX(-6px);
        pointer-events: none;
      }
    }

    @media (max-width: 768px) {
      font-size: $font-base-sm;
    }
  }
}

// ─────────────────────────────────────────────────────────────────
// INFORMATIVA HEADER
// ─────────────────────────────────────────────────────────────────
.informativa-header {
  margin-bottom: clamp(2rem, 4vw, 3rem);
  text-align: center;

  @media (max-width: 768px) {
    margin-bottom: $space-2;
  }

  @media (max-width: 480px) {
    margin-bottom: $space-1-5;
  }

  &__eyebrow {
    position: relative;
    display: inline-block;
    font-size: $font-eyebrow;
    font-weight: 600;
    text-transform: uppercase;
    color: $clr-tertiary;
    margin-left: 60px;
    margin-bottom: $space-0-5;
    white-space: nowrap;
    transition: all 0.3s ease;
    

    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: -50px;
      width: 40%;
      height: 12px;
      background-color: rgba($clr-tertiary, 0.7);
      transition: all 0.3s ease;
    }

    @media (hover: hover) {
      &:hover {
        color: $clr-tertiary;
        &::before {
          background-color: rgba($clr-tertiary, 0.8);
          width: 110%;
          transform: scaleY(1.2);
        }
      }
    }

    @media (max-width: 920px) {
      font-size: $font-list-meta-sm;
      margin-left: 0;
      white-space: normal;
    }
    @media (max-width: 480px) {
      font-size: $font-eyebrow-xs;
      margin-bottom: $space-0-25;
    }
    @media (max-width: 360px) {
      font-size: $font-eyebrow-xxs;
    }
  }

  &__title {
    font-size: $font-title;
    font-weight: 300;
    color: $text-color;
    margin: 0;
    margin-left: 60px;
    margin-bottom: $space-2;

    @media (max-width: 920px) {
      font-size: $font-title-sm;
      margin-left: 0;
    }
    @media (max-width: 480px) {
      font-size: $font-title-xs;
    }
    @media (max-width: 360px) {
      font-size: $font-title-xxs;
    }
  }

  &__date {
    font-size: $font-list-meta;
    color: $clr-text-light;
    margin-top: $space-1;

    @media (max-width: 768px) {
      font-size: $font-list-meta-sm;
      margin-top: $space-0-75;
    }
    @media (max-width: 480px) {
      font-size: $font-list-meta-xs;
      margin-top: $space-0-5;
    }
  }
}

// ─────────────────────────────────────────────────────────────────
// CAROUSEL
// ─────────────────────────────────────────────────────────────────
.informativa-image-carousel {
  width: 100%;
  margin-bottom: $space-3;
  text-align: center;

  @media (max-width: 768px) {
    margin-bottom: $space-2;
  }

  @media (max-width: 480px) {
    margin-bottom: $space-1-5;
  }
}

.carousel-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  @include flex-gap(1rem);

  @media (max-width: 768px) {
    @include flex-gap(0.75rem);
  }

  @media (max-width: 480px) {
    @include flex-gap(0.5rem);
  }
}

.carousel-image-wrapper,
.single-image-wrapper {
  flex: 1;
  max-width: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin: 0 auto;

  @media (min-width: 1025px) and (max-width: 1300px) {
    max-width: 430px;
  }

  @media (max-width: 1024px) {
    max-width: 100%;
  }
}

.carousel-image {
  width: 100%;
  height: auto;
  display: block;
  box-shadow: 0 4px 16px rgba($clr-brand-dark, 0.1);
  transition: all 0.3s ease;
  border-radius: 4px;

  @media (hover: hover) {
    &:hover {
      transform: scale(1.02);
      box-shadow: 0 8px 24px rgba($clr-brand-dark, 0.15);
    }
  }
}

.carousel-arrow {
  position: absolute;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba($clr-brand-dark, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
  flex-shrink: 0;
  outline: none;
  padding: 0;

  svg {
    width: 24px;
    height: 24px;
    color: $clr-brand-dark;
  }

  @media (hover: hover) {
    &:hover {
      background-color: rgba(255, 255, 255, 1);
      transform: scale(1.1);
      box-shadow: 0 4px 12px rgba($clr-brand-dark, 0.15);
    }
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
  }
  /* ricaduta iOS < 15.4: senza :focus-visible la regola sopra viene
     scartata per intero. Si ripiega su :focus, che mostra l'anello
     anche col mouse: e' il degrado accettato. */
  .no-focusvisible &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }

  &.carousel-arrow-left {
    left: -3rem;
  }

  &.carousel-arrow-right {
    right: -3rem;
  }

  @media (max-width: 1024px) {
    width: 50px;
    height: 50px;

    svg {
      width: 20px;
      height: 20px;
    }

    &.carousel-arrow-left {
      left: -2.5rem;
    }

    &.carousel-arrow-right {
      right: -2.5rem;
    }
  }

  @media (max-width: 768px) {
    width: 45px;
    height: 45px;

    svg {
      width: 18px;
      height: 18px;
    }

    &.carousel-arrow-left {
      left: -2rem;
    }

    &.carousel-arrow-right {
      right: -2rem;
    }
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;

    svg {
      width: 16px;
      height: 16px;
    }

    &.carousel-arrow-left {
      left: 0.5rem;
    }

    &.carousel-arrow-right {
      right: 0.5rem;
    }
  }
}

.carousel-dots {
  display: flex;
  justify-content: center;
  @include flex-gap(0.75rem);
  margin-top: 1.5rem;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background-color: rgba($clr-text-light, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;

  @media (hover: hover) {
    &:hover {
      background-color: rgba($clr-text-light, 0.5);
      transform: scale(1.2);
    }
  }

  &.active {
    background-color: $clr-tertiary;
    width: 24px;
    border-radius: 5px;
  }
}

// ─────────────────────────────────────────────────────────────────
// OVERRIDE SECTION HTML TEXT WIDTH FOR INFORMATIVE LAYOUT
// ─────────────────────────────────────────────────────────────────
:deep(.html-text-section > .container) {
  max-width: 1600px;
}

// ─────────────────────────────────────────────────────────────────
// SECTIONS
// ─────────────────────────────────────────────────────────────────
.informativa-sections {
  margin: $space-2 0;
}
</style>